'use client'

import { Link } from '@/i18n/routing'
import { COLORS } from '@/utils/constants/colors'
import { STRIPE_CERTIFICATIONS } from '@/utils/seo/stripe-certifications'
import { IconBrandFacebook, IconBrandInstagram, IconBrandLinkedin, IconBrandUnsplash, IconBrandXing, IconMessage } from '@tabler/icons-react'
import { useLocale, useTranslations } from 'next-intl'
import Image from 'next/image'
import NextLink from 'next/link'
import ArrowRightIcon from '../icons/ArrowRightIcon'
import Card from '../ui/Card'
import StripePartnerBadge from '../ui/StripePartnerBadge'

const Footer = () => {
  const t = useTranslations('footer')
  const tPartner = useTranslations('partner')
  const locale = useLocale()
  return (
    <footer className="max-w-[1440px] w-full [@media(max-width:1440px)]:px-8 relative mx-auto mb-16">
      <div className="grid grid-cols-2 lg:grid-cols-12 gap-8">
        <Link href="/" className="col-span-full lg:col-span-3">
          <Card className="break-all h-full p-16 bg-KMRS-Dark flex justify-center items-center">
            <Image src="/images/logos/kommers-logo-large.svg" width={212} height={52} alt="KOMMERS Logo" />
          </Card>
        </Link>
        <Card className="break-all col-span-full mobile-l:col-span-1 lg:col-span-3 p-8 bg-KMRS-Light">
          <ArrowRightIcon className="w-5 h-5" size={20} color={COLORS['KMRS-Darkmode']} />
          <ul className="text-KMRS-Darkmode grid laptop:grid-cols-2 gap-x-6 mt-8 leading-6 text-lg font-light ">
            <li>
              <a className="flex flex-row items-center gap-x-2" href="https://www.linkedin.com/company/kommers-io/" target="_blank" rel="noopener noreferrer">
                <IconBrandLinkedin className="text-accent" size={13} />
                LinkedIn
              </a>
            </li>
            <li>
              <a className="flex flex-row items-center gap-x-2" href="https://www.xing.com/pages/kommers-ug-haftungsbeschraenkt" target="_blank" rel="noopener noreferrer">
                <IconBrandXing className="text-accent" size={13} />
                Xing
              </a>
            </li>
            <li>
              <a className="flex flex-row items-center gap-x-2" href="https://www.facebook.com/kommers.io/" target="_blank" rel="noopener noreferrer">
                <IconBrandFacebook className="text-accent" size={13} />
                Facebook
              </a>
            </li>
            <li>
              <a className="col-start-2 flex flex-row items-center gap-x-2" href="https://www.instagram.com/kommers.io/" target="_blank" rel="noopener noreferrer">
                <IconBrandInstagram className="text-accent" size={13} /> Instagram
              </a>
            </li>
            <li>
              <a className="col-start-2 flex flex-row items-center gap-x-2" href="https://unsplash.com/de/@kommers/" target="_blank" rel="noopener noreferrer">
                <IconBrandUnsplash className="text-accent" size={13} />
                Unsplash
              </a>
            </li>
          </ul>
        </Card>
        <Card className="break-all col-span-full mobile-l:col-span-1 lg:col-span-3 p-8 bg-KMRS-Light">
          <ArrowRightIcon className="w-5 h-5" size={20} color={COLORS['KMRS-Darkmode']} />
          <ul className="text-KMRS-Darkmode mt-8 leading-6 text-lg font-light lg:mt-auto">
            <li>
              <Link href="/cookie-richtlinien">{t('cookies')}</Link>
            </li>
            <li>
              <Link href="/datenschutz">{t('privacy')}</Link>
            </li>
            <li>
              <Link href="/impressum">{t('imprint')}</Link>{' '}
            </li>
          </ul>
        </Card>

        <Card className="break-all col-span-full md:col-span-2 lg:col-span-3  p-8 bg-KMRS-Light">
          <div className="w-full flex flex-row items-center gap-8 flex-wrap laptop:flex-nowrap mt-auto">
            <a href="mailto:hello@kommers.io" className="bg-accent px-8 py-6 rounded-xl flex justify-center items-center">
              <IconMessage size={20} color="#fff" />
              <span className="sr-only">{t('email')}</span>
            </a>
            <p className="text-KMRS-Darkmode  leading-6 text-lg font-light">
              ©{new Date().getFullYear()} KOMMERS
              <br />
              <a href="tel:+4991147734580">+49 (911) 477 345 80</a>
              <br />
              <a href="mailto:hello@kommers.io">hello@kommers.io</a>
              <br />
            </p>
          </div>
        </Card>
      </div>
      {/* Local Business Citations for SEO */}
      <div className="mt-16 pt-8 border-t border-KMRS-Dark/20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm text-white/60">
          <div>
            <h3 className="font-medium text-white mb-2">KOMMERS GmbH</h3>
            <address className="not-italic">
              <span>Benno-Strauß-Straße 5</span>
              <br />
              <span>90763</span> <span>Fürth</span>
              <br />
              <span>Bayern</span>, <span>Deutschland</span>
            </address>
            <p className="mt-2">
              <a href="tel:+4991147734580" className="hover:text-accent transition-colors">
                +49 (911) 477 345 80
              </a>
              <br />
              <a href="mailto:hello@kommers.io" className="hover:text-accent transition-colors">
                hello@kommers.io
              </a>
            </p>
          </div>
          <div>
            <h3 className="font-medium text-white mb-2">{t('serviceAreas')}</h3>
            <ul className="space-y-1">
              <li>
                {/* i18n-Link mappt den DE-Pfad-Key automatisch auf /en/agency/web-agency-fuerth.
                    Vorher wurde /en/agentur/webagentur-fuerth handgebaut -> existiert als Route nicht. */}
                <Link href="/agentur/webagentur-fuerth" className="hover:text-accent transition-colors">
                  {t('webAgencyFuerth')}
                </Link>
              </li>
              <li>
                <Link href="/agentur/webagentur-nuernberg" className="hover:text-accent transition-colors">
                  {t('webAgencyNuremberg')}
                </Link>
              </li>
              <li>
                <Link href="/agentur" className="hover:text-accent transition-colors">
                  {t('digitalAgencyRegion')}
                </Link>
              </li>
              <li>
                <NextLink href={locale === 'en' ? '/en/lp/e-commerce-agency' : '/lp/e-commerce-agentur'} className="hover:text-accent transition-colors">
                  {t('ecommerceAgencyBavaria')}
                </NextLink>
              </li>
              <li>
                <NextLink href={locale === 'en' ? '/en/lp/online-store-development' : '/lp/online-shop-entwicklung'} className="hover:text-accent transition-colors">
                  {locale === 'en' ? 'Online Store Development' : 'Online-Shop Entwicklung'}
                </NextLink>
              </li>
              <li>
                <NextLink href={locale === 'en' ? '/en/lp/shopify-agency' : '/lp/shopify-agentur'} className="hover:text-accent transition-colors">
                  {t('shopifyAgency')}
                </NextLink>
              </li>
              <li>
                <NextLink href={locale === 'en' ? '/en/lp/shopify-support' : '/lp/shopify-betreuung'} className="hover:text-accent transition-colors">
                  {t('shopifySupport')}
                </NextLink>
              </li>
              <li>
                <NextLink href={locale === 'en' ? '/en/lp/woocommerce-agency' : '/lp/woocommerce-agentur'} className="hover:text-accent transition-colors">
                  {t('woocommerceAgency')}
                </NextLink>
              </li>
              <li>
                <NextLink href={locale === 'en' ? '/en/lp/wordpress-agency' : '/lp/wordpress-agentur'} className="hover:text-accent transition-colors">
                  {t('wordpressAgency')}
                </NextLink>
              </li>
              <li>
                <NextLink href={locale === 'en' ? '/en/lp/shopware-agency' : '/lp/shopware-agentur'} className="hover:text-accent transition-colors">
                  {t('shopwareAgency')}
                </NextLink>
              </li>
              <li>
                <NextLink href={`/${locale === 'en' ? 'en/' : ''}lp/sap-commerce-cloud`} className="hover:text-accent transition-colors">
                  {t('sapCommerceCloud')}
                </NextLink>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium text-white mb-2">{t('standardsMembership')}</h3>
            <ul className="space-y-1">
              {/* Partnerstatus und Zertifizierungen stehen vor den allgemeinen Standards,
                  weil sie die extern verifizierbaren Signale dieser Liste sind. Jeder Eintrag
                  verlinkt auf seinen Beleg, damit die Behauptung nachprüfbar bleibt. */}
              <li>
                <a href="https://www.brevo.com/expert-directory/partner/kommers-gmbh/" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-accent transition-colors">
                  {tPartner('brevoTitle')}
                </a>
              </li>
              {STRIPE_CERTIFICATIONS.map((certification) => (
                <li key={certification.key}>
                  <a href={certification.url} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-accent transition-colors">
                    {tPartner(`${certification.key}Title`)}
                  </a>
                </li>
              ))}
              <li>
                <a href="https://www.ihk-nuernberg.de/" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">
                  {t('ihkMember')}
                </a>
              </li>
            </ul>
            {/* Das offizielle Stripe-Partner-Badge steht unter den Zertifizierungen, weil es
                denselben Nachweis als Bildmarke zeigt. Es fuehrt auf /stripe-partner, wo die
                vier Zertifikate einzeln belegt sind. Laut Co-Branding-Guidelines darf das
                Badge nicht veraendert werden, deshalb kein Filter und kein Hover-Effekt. */}
            <Link href="/stripe-partner" className="mt-6 inline-block">
              <StripePartnerBadge width={170} />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
